import { EDIT_MESSAGE_STATUS, IMessage } from "@home-health-notify/domain";
import CampaignIcon from "@mui/icons-material/Campaign";
import {
  ACTIVE_MESSAGE_STATUS,
  formatChatMessageTime,
  useTranslation,
} from "hhn-commons";
import {
  MessageFooterFirstRow,
  MessageFooterSecondRow,
  MessageFooterStyle,
  MessageRetryButton,
  MessageSendFailedLabel,
  MessageStatusSeparator,
  MessageTimeComponent,
  StarredMessageIcon,
} from "../messageType/MessageContentStyle";
import { MessageVisibilityStatus } from "./MessageVisibilityStatus";
import MessagePendingStatus from "../messagePanelMenu/recipientInfo/messageStatus/MessagePendingStatus";
import {
  MessageFailedIcon,
  MessageStatusBox,
} from "../messagePanelMenu/recipientInfo/messageStatus/MessageStatus.style";
import React from "react";

interface MessageFooterProps {
  isMessageStarred: boolean;
  isShowBroadcastIcon: boolean;
  message: IMessage;
  messageStatus?: ACTIVE_MESSAGE_STATUS;
  onRetrySend?: () => void;
}

export const MessageFooter = ({
  isMessageStarred,
  isShowBroadcastIcon,
  message,
  messageStatus,
  onRetrySend,
}: MessageFooterProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  // A send with nothing left to retry is one the server actively refused —
  // tracking is dropped the moment a non-200 ack comes back. "Not sent" would
  // understate that: it is not waiting on anything, it was rejected.
  const isSendFailed = messageStatus === ACTIVE_MESSAGE_STATUS.FAILED;

  const messageData = message.getMessage();
  const isEdited = message.isEdited();
  const isEditFailed = messageData.editStatus === EDIT_MESSAGE_STATUS.FAILED;

  return (
    <MessageFooterStyle>
      <MessageFooterFirstRow>
        {/* Shares MessageStatusBox's absolute slot with the pending indicator
            so the two can never render on top of each other. */}
        {isSendFailed ? (
          <MessageStatusBox data-testid="message-send-failed">
            <MessageFailedIcon />
            <MessageSendFailedLabel>
              {tNs(
                isEditFailed
                  ? "message.editFailedStatus"
                  : onRetrySend
                    ? "message.sendFailedStatus"
                    : "message.sendRejectedStatus",
              )}
            </MessageSendFailedLabel>
            {onRetrySend && (
              <>
                <MessageStatusSeparator>·</MessageStatusSeparator>
                <MessageRetryButton type="button" onClick={onRetrySend}>
                  {tNs("message.sendFailedAction")}
                </MessageRetryButton>
              </>
            )}
          </MessageStatusBox>
        ) : (
          messageStatus && getMessageStatusIcon(messageStatus)
        )}
        {isShowBroadcastIcon && (
          <CampaignIcon color="secondary" fontSize="small" />
        )}
        {isMessageStarred && (
          <StarredMessageIcon data-testid="star-icon" color="secondary" />
        )}
        <MessageTimeComponent variant="caption">
          {isEdited && <> {tNs("edited")} · </>}
          {formatChatMessageTime(messageData.createdAt)}
        </MessageTimeComponent>
      </MessageFooterFirstRow>
      <MessageFooterSecondRow>
        <MessageVisibilityStatus message={message}></MessageVisibilityStatus>
      </MessageFooterSecondRow>
    </MessageFooterStyle>
  );
};

export function getMessageStatusIcon(
  messageStatus: ACTIVE_MESSAGE_STATUS,
): React.ReactNode {
  switch (messageStatus) {
    case ACTIVE_MESSAGE_STATUS.PENDING:
      return <MessagePendingStatus />;
    default:
      return <></>;
  }
}
